import React, { useEffect, useMemo, useState } from 'react'

import { ICellEditorParams } from 'ag-grid-community'
import Select from 'react-select'
import { toast } from 'react-toastify'

import ClosedLostDrawer from '../ClosedLossDrawer'
import { setClosedLossDrawerOpen } from '@/client/generalSlice'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import {
  setIsKickBackModalOpen,
  setKickBackApplication,
  setKickBackModalClosedCallback,
  setStages,
} from '@/features/DealKickBack/kickBackSlice'
import { useUpdateSubStageMutation } from '@/services/application'
import { useGetEscalationsQuery } from '@/services/escalations'
import { useGetLoggedUserQuery } from '@/services/user'
import {
  useGetChecklistGroupsQuery,
  useUpdateItemsFromTemplateMutation,
} from '@/services/v2-application/checklistGroup'
import {
  calcIsSelectDisabled,
  customStyles,
  shouldOpenKickBackModal,
  useGenerateOptions,
} from '@/utils/StageSelector'

type StageSelectorProps = {
  application: any
  parentCellProps?: ICellEditorParams
}

export const WQStageSelector = ({ application, parentCellProps }: StageSelectorProps) => {
  const dispatch = useAppDispatch()
  const [updateSubStage, { isLoading: isUpdating }] = useUpdateSubStageMutation()
  const [updateItemsFromTemplate] = useUpdateItemsFromTemplateMutation()
  const isClosedLossModal = useAppSelector((state) => state.general.closedLossDrawerOpen)

  const { data: user, isLoading: isLoadingUser } = useGetLoggedUserQuery()
  const [actualState, setActualState] = useState(application?.application_state)
  const [desiredState, setDesiredState] = useState(Object)

  const [hasEscalationsNotApproved, setHasEscalationsNotApproved] = useState(false)

  const { data: checklist_groups } = useGetChecklistGroupsQuery(application?.application_uuid, {
    skip: !application?.application_uuid,
  })
  const { data: escalations } = useGetEscalationsQuery(application?.external_id, {
    skip: !application?.external_id,
  })

  const isDisabled = useMemo(
    () => calcIsSelectDisabled(user, application, actualState),
    [application]
  )

  useEffect(() => {
    setHasEscalationsNotApproved(escalations?.some((escalation) => !escalation.approved))
  }, [escalations])

  const options = useGenerateOptions(
    application?.application_uuid,
    application?.application_state,
    application?.application_state_index,
    user,
    isLoadingUser,
    hasEscalationsNotApproved
  )

  const updateParentCellValue = (applicationStage: string) => {
    parentCellProps?.node.setData({
      ...parentCellProps?.node.data,
      application_state: applicationStage,
    })
    parentCellProps?.stopEditing()
  }

  const handleChange = async (option) => {
    const actual = options.find((it) => it.value === actualState)

    if (shouldOpenKickBackModal(option, user, actualState)) {
      dispatch(setKickBackApplication(application?.external_id))
      dispatch(setKickBackModalClosedCallback((stage: string) => updateParentCellValue(stage)))
      dispatch(setIsKickBackModalOpen(true))
      dispatch(setStages({ from: actual, to: option }))
      return
    }
    if (option.value !== 'closed-lost') {
      const destinationGroup = checklist_groups?.results?.find(
        (item) => item.type_name === option.label
      )
      if (destinationGroup && option.index > application.application_state_index) {
        // Update checklist group with the latest items from template
        await updateItemsFromTemplate({
          applicationId: application.uuid,
          checklistGroupId: destinationGroup.uuid,
        })
      }
      const response = await updateSubStage({
        external_id: application.external_id,
        application_state: option.value,
      })
      if ('data' in response) {
        setActualState(response?.data?.application_state)
        updateParentCellValue(response?.data?.application_state)
      }

      if ('error' in response) {
        const errorDetail =
          (response?.error && 'data' in response.error && (response.error as any).data?.detail) ||
          'Something went wrong.'
        toast.error(errorDetail, {
          containerId: 'global',
          position: 'top-center',
        })
        return
      }
    } else {
      dispatch(setClosedLossDrawerOpen(true))
    }
  }

  return (
    <div className='w-full'>
      <ClosedLostDrawer
        isOpen={isClosedLossModal}
        application={application}
        setActualState={setActualState}
        modalCloseCallback={() => updateParentCellValue('closed-lost')}
      />
      <div className='p-4 flex'>
        <Select
          // Force updating when options load or selection changes
          key={options.toString() + application.application_state}
          instanceId='category-select'
          options={options}
          defaultValue={() => {
            const initialOption = options.find((it) => it.value === application?.application_state)
            setDesiredState(initialOption)
            return initialOption
          }}
          onChange={(option) => {
            setDesiredState(option)
          }}
          isDisabled={isUpdating || isDisabled}
          isOptionDisabled={(option) => option.disabled}
          isLoading={isUpdating}
          isSearchable={false}
          className='font-semibold text-xl z-30 w-64'
          styles={customStyles}
          value={options.find((it) => it.value === desiredState?.value)}
        />
      </div>

      <div className='m-2 flex justify-end'>
        <button
          className='btn-primary-legacy w-1/4'
          type='submit'
          onClick={() => {
            handleChange(desiredState)
          }}
        >
          Submit
        </button>
      </div>
    </div>
  )
}
